import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";

function Cart() {
  const navigate = useNavigate();
  const {
    cartItems,
    designCart,
    increment,
    decrement,
    removeFromCart,
    removeFromDesignCart,
    total,
  } = useCart();

  const [expandedDesigns, setExpandedDesigns] = useState({});

  // 🧩 Combine cookie + localStorage items
  const combinedCart = useMemo(() => {
    return [
      ...cartItems.map((item) => ({ ...item, type: "normal" })),
      ...designCart.map((item) => ({ ...item, type: "design" })),
    ];
  }, [cartItems, designCart]);
  
  // 🧮 Combined total (products + designs)
  const combinedTotal =
    total + designCart.reduce((sum, item) => sum + (item.price || 0), 0);

  const handleGoProduct = () => navigate("/");
  const handleGoCheckout = () => navigate("/checkout");

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "https://via.placeholder.com/150x150?text=No+Image";
    if (imagePath.startsWith("data:image")) return imagePath;
    if (imagePath.startsWith("/uploads")) return imagePath;
    if (imagePath.startsWith("http")) return imagePath;
    return "https://via.placeholder.com/150x150?text=Invalid+Image";
  };

  const toggleDesignView = (itemId) => {
    setExpandedDesigns((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const isCustomDesign = (item) =>
    item.customDesign &&
    (item.customDesign.frontImage || item.customDesign.backImage);

  return (
    <div className="min-h-screen bg-white text-gray-900 font-custom py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold mb-10 text-center md:text-left">
          Your Cart{" "}
          <span className="text-gray-400">({combinedCart.length})</span>
        </h1>

        {combinedCart.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl text-gray-400">Your cart is empty.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {combinedCart.map((item) => (
              <div
                key={item.id}
                className="flex flex-col p-6 bg-gray-50 rounded-xl shadow-lg border border-gray-200"
              >
                {/* Custom Design Badge */}
                {isCustomDesign(item) && (
                  <div className="mb-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                      Custom Design
                    </span>
                  </div>
                )}

                <div className="flex flex-col md:flex-row items-center justify-between">
                  {/* Image Section */}
                  <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-6 w-full md:w-auto">
                    {isCustomDesign(item) ? (
                      <div className="flex flex-col items-center space-y-2">
                        <div className="flex space-x-2">
                          {item.customDesign.frontImage && (
                            <img
                              src={item.customDesign.frontImage}
                              alt="Front Design"
                              className="w-28 h-28 object-cover rounded-xl shadow-lg border-2 border-blue-200"
                            />
                          )}
                          {item.customDesign.backImage && (
                            <img
                              src={item.customDesign.backImage}
                              alt="Back Design"
                              className="w-28 h-28 object-cover rounded-xl shadow-lg border-2 border-blue-200"
                            />
                          )}
                        </div>
                        <button
                          onClick={() => toggleDesignView(item.id)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
                        >
                          {expandedDesigns[item.id]
                            ? "Show Less"
                            : "View Full Design"}
                        </button>
                      </div>
                    ) : (
                      <img
                        src={getImageUrl(item.image)}
                        alt={item.name}
                        className="w-28 h-28 object-cover rounded-xl shadow-lg"
                      />
                    )}

                    {/* Product Details */}
                    <div className="text-center md:text-left">
                      <h2 className="font-bold text-xl md:text-2xl mb-1">
                        {item.name}
                      </h2>
                      <p className="text-sm text-gray-500">
                        {item.description}
                      </p>

                      {/* Design Details */}
                      {isCustomDesign(item) && (
                        <div className="mt-2 space-y-1">
                          <p className="text-xs text-gray-600">
                            <span className="font-semibold">Type:</span>{" "}
                            {item.customDesign.typeName}
                          </p>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-600 font-semibold">
                              Color:
                            </span>
                            <div
                              className="w-5 h-5 rounded-full border-2 border-gray-300 shadow-sm"
                              style={{
                                backgroundColor: item.customDesign.color,
                              }}
                            />
                          </div>
                        </div>
                      )}

                      <p className="text-lg font-bold mt-2 text-[#6cc5bf]">
                        ${item.price?.toFixed(2) ?? "0.00"}
                      </p>
                    </div>
                  </div>

                  {/* Quantity & Remove */}
                  <div className="flex items-center space-x-6 mt-6 md:mt-0">
                    {item.type === "normal" ? (
                      <div className="flex items-center border border-[#6cc5bf] rounded-full">
                        <button
                          className="text-2xl px-3 py-1 text-gray-400 hover:text-[#6cc5bf] transition-colors"
                          onClick={() => decrement(item.id)}
                        >
                          −
                        </button>
                        <span className="px-3 text-lg font-semibold">
                          {item.quantity}
                        </span>
                        <button
                          className="text-2xl px-3 py-1 text-gray-400 hover:text-[#6cc5bf] transition-colors"
                          onClick={() => increment(item.id)}
                        >
                          +
                        </button>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">Custom Item</div>
                    )}

                    <div className="font-bold text-lg md:text-xl">
                      ${(item.price * (item.quantity ?? 1)).toFixed(2)}
                    </div>
                    <button
                      className="text-sm text-red-500 hover:text-red-400 underline transition-colors hidden md:block"
                      onClick={() =>
                        item.type === "normal"
                          ? removeFromCart(item.id)
                          : removeFromDesignCart(item.id)
                      }
                    >
                      Remove
                    </button>
                  </div>

                  <button
                    className="text-sm text-red-500 hover:text-red-400 underline mt-4 md:hidden"
                    onClick={() =>
                      item.type === "normal"
                        ? removeFromCart(item.id)
                        : removeFromDesignCart(item.id)
                    }
                  >
                    Remove
                  </button>
                </div>

                {/* Expanded Design View */}
                {isCustomDesign(item) && expandedDesigns[item.id] && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-700 mb-4">
                      Full Design Preview
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {item.customDesign.frontImage && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-gray-600 text-center">
                            Front Design
                          </p>
                          <img
                            src={item.customDesign.frontImage}
                            alt="Front Design Full"
                            className="w-full rounded-lg shadow-md border border-gray-200"
                          />
                        </div>
                      )}
                      {item.customDesign.backImage && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-gray-600 text-center">
                            Back Design
                          </p>
                          <img
                            src={item.customDesign.backImage}
                            alt="Back Design Full"
                            className="w-full rounded-lg shadow-md border border-gray-200"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Footer Totals */}
        {combinedCart.length > 0 && (
          <div className="mt-12 pt-8 border-t border-gray-300 flex flex-col md:flex-row justify-between items-center">
            <div className="text-lg font-bold">
              Subtotal:{" "}
              <span className="text-2xl text-[#6cc5bf]">
                ${combinedTotal.toFixed(2)}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 mt-6 md:mt-0 w-full md:w-auto">
              <button
                onClick={handleGoProduct}
                className="w-full sm:w-auto bg-gray-200 hover:bg-gray-300 text-gray-900 font-semibold px-8 py-3 rounded-lg transition-colors"
              >
                Browse More
              </button>
              <button
                onClick={handleGoCheckout}
                className="w-full sm:w-auto bg-[#6cc5bf] hover:bg-[#5ab4ae] text-white font-bold px-8 py-3 rounded-lg shadow-lg transition-colors"
              >
                Proceed To Checkout
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Cart;
